import { Injectable } from '@angular/core';
import { Observable, of, Subscriber } from 'rxjs';
import { DeleteInterface } from './delete-interface';
import {
  AdmUsersService,
  AiAssessmentsService,
  AppAssessmentsService,
  ApplicationService,
  ApplicationTypeService,
  AppointmentsAuthorizedService,
  AppointmentsResponsibleService,
  AppointmentsSystemAdminService,
  AttachmentsService,
  CovidAssessmentsService,
  DamageService,
  DataBreachProcessIncidentReportsService,
  DataBreachProcessService,
  DeleteEntityServiceInterface,
  DepartmentService,
  EmploymentRelationshipService,
  FolderService,
  GlobalMeasureChecksService,
  InterestedPersonCategoryService,
  LegalBasisService,
  LegalEntityService,
  NatureOfDataService,
  NoticeService,
  NotificationsService,
  OrganizationalFunctionService,
  PermissionRulesService,
  PreliminaryRiskAssessmentService,
  PrivacyRoleService,
  PurposeService,
  QuestionService,
  RecipientService,
  RegulationService,
  SecurityMeasureCategoryService,
  SecurityMeasureService,
  SupplierService,
  TechnicalMeasureCategoryService,
  TechnicalMeasureService,
  TreatmentCategoryService,
  TreatmentService,
  UserGroupsService,
  UsersResourcesService,
  UsersService,
} from '@pt/api-client';
import { DialogType, PtEntity, UiDocumentItem } from '@pt/pt-models';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { DialogService } from './dialog.service';

@Injectable({
  providedIn: 'root',
})
export class DeleteService implements DeleteInterface {
  constructor(
    private store: Store,
    private dialogService: DialogService,
    private translateService: TranslateService,
    // DOCUMENT SERVICE
    private treatmentService: TreatmentService,
    private appAssessmentService: AppAssessmentsService,
    private aiAssessmentService: AiAssessmentsService,
    private globalMeasureChecksService: GlobalMeasureChecksService,
    private covidAssessmentsService: CovidAssessmentsService,
    private folderService: FolderService,
    // ENTITY SERVICE
    private attachmentService: AttachmentsService,
    private userGroupService: UserGroupsService,
    private permissionRulesService: PermissionRulesService,
    private questionService: QuestionService,
    private dataBreachProcessService: DataBreachProcessService,
    private dataBreachNoticeService: DataBreachProcessIncidentReportsService,
    private legalEntityService: LegalEntityService,
    private organizationalFunctionService: OrganizationalFunctionService,
    private usersResourcesService: UsersResourcesService,
    private privacyRoleService: PrivacyRoleService,
    private employmentRelationshipService: EmploymentRelationshipService,
    private treatmentCategoryService: TreatmentCategoryService,
    private applicationService: ApplicationService,
    private applicationTypeService: ApplicationTypeService,
    private regulationService: RegulationService,
    private noticeService: NoticeService,
    private natureOfDataService: NatureOfDataService,
    private interestedPersonCategoryService: InterestedPersonCategoryService,
    private recipientService: RecipientService,
    private supplierService: SupplierService,
    private damageService: DamageService,
    private appointmentsSystemAdminService: AppointmentsSystemAdminService,
    private appointmentsResponsibleService: AppointmentsResponsibleService,
    private appointmentsAuthorizedService: AppointmentsAuthorizedService,
    private purposeService: PurposeService,
    private legalBasisService: LegalBasisService,
    private organizationalMeasureService: SecurityMeasureService,
    private organizationalMeasureCategoryService: SecurityMeasureCategoryService,
    private preliminaryRiskAssessmentService: PreliminaryRiskAssessmentService,
    private technicalMeasureService: TechnicalMeasureService,
    private technicalMeasureCategoryService: TechnicalMeasureCategoryService,
    private usersService: UsersService,
    private employmentRelationship: EmploymentRelationshipService,
    private notificationsService: NotificationsService,
    private departmentService: DepartmentService,
    // UI CONSOLE
    private admUsersService: AdmUsersService
  ) {}

  public deleteEntity(
    entityType: PtEntity,
    entities: any[],
    singleEntityName?: string
  ): Observable<boolean> {
    const deleteEntityConfiguration = this.getEntityService(
      entityType,
      singleEntityName
    );
    if (deleteEntityConfiguration && entities?.length > 0) {
      if (entities?.length === 1) {
        return this.deleteSingleEntity(
          entityType,
          deleteEntityConfiguration,
          entities[0]
        );
      } else {
        return this.deleteMultipleEntity(
          entityType,
          deleteEntityConfiguration,
          entities
        );
      }
    }
    return of(false);
  }

  public deleteDocument(
    documentType: PtEntity,
    documents: UiDocumentItem[]
  ): Observable<boolean> {
    const documentConfiguration = this.getDocumentService(documentType);
    if (documentConfiguration && documents?.length > 0) {
      if (documents?.length === 1) {
        return this.deleteSingleDocument(
          documentType,
          documentConfiguration,
          documents[0]
        );
      } else {
        return this.deleteMultipleDocument(
          documentType,
          documentConfiguration,
          documents
        );
      }
    }
    return of(false);
  }

  private deleteSingleEntity(
    entityType: PtEntity,
    deleteEntityConfiguration: DeleteEntityConfiguration,
    entity: {
      id: number;
    }
  ): Observable<boolean> {
    return new Observable<boolean>((observable: Subscriber<boolean>) => {
      switch (entityType) {
        case 'globalMeasureCheck':
          this.dialogService
            .runConfirmDialogWithValidation({
              title: deleteEntityConfiguration.title,
              message: deleteEntityConfiguration.message,
              dialogType: DialogType.DELETE,
              size: 'md',
            })
            .subscribe((confirmed) => {
              if (confirmed) {
                this.globalMeasureChecksService.delete(entity.id).subscribe(
                  (s) => {
                    observable.next(true);
                    // this.showSuccessfullyDeleteNotification();
                  },
                  () => {
                    this.showFailedDeleteNotification();
                    observable.next(false);
                  }
                );
              }
            });
          break;
        case 'incidentReport':
          this.dialogService
            .runConfirmDialogWithValidation({
              title: deleteEntityConfiguration.title,
              message: deleteEntityConfiguration.message,
              dialogType: DialogType.DELETE,
              size: 'md',
            })
            .subscribe((confirmed) => {
              if (confirmed) {
                this.dataBreachNoticeService.delete(entity.id).subscribe(
                  (s) => {
                    observable.next(true);
                    // this.showSuccessfullyDeleteNotification();
                  },
                  () => {
                    this.showFailedDeleteNotification();
                    observable.next(false);
                  }
                );
              }
            });
          break;
        case 'dataBreach':
          this.dialogService
            .runConfirmDialogWithValidation({
              title: deleteEntityConfiguration.title,
              message: deleteEntityConfiguration.message,
              dialogType: DialogType.DELETE,
              size: 'md',
            })
            .subscribe((confirmed) => {
              if (confirmed) {
                this.dataBreachProcessService.delete(entity.id).subscribe(
                  (s) => {
                    observable.next(true);
                    // this.showSuccessfullyDeleteNotification();
                  },
                  () => {
                    this.showFailedDeleteNotification();
                    observable.next(false);
                  }
                );
              }
            });
          break;
        case 'users':
        case 'userGroups':
        case 'consoleUsers':
        case 'notifications':
        case 'folder':
        case 'attachment':
          this.dialogService
            .runConfirmDialog({
              title: deleteEntityConfiguration.title,
              message: deleteEntityConfiguration.message,
              dialogType: DialogType.DELETE,
            })
            .subscribe((confirmed) => {
              if (confirmed) {
                deleteEntityConfiguration.service.delete(entity.id).subscribe(
                  () => {
                    observable.next(true);
                    this.showSuccessfullyDeleteNotification();
                  },
                  () => {
                    this.showFailedDeleteNotification();
                    observable.next(false);
                  }
                );
              } else {
                observable.next(false);
              }
            });
          break;
        default:
          this.dialogService
            .runConfirmDeleteDataProtectionGovernanceItemDialog(
              deleteEntityConfiguration.title,
              deleteEntityConfiguration.message,
              deleteEntityConfiguration.service,
              entity.id,
              entityType
            )
            .subscribe((confirmed) => {
              if (confirmed) {
                deleteEntityConfiguration.service.delete(entity.id).subscribe(
                  () => {
                    observable.next(true);
                    this.showSuccessfullyDeleteNotification();
                  },
                  () => {
                    this.showFailedDeleteNotification();
                    observable.next(false);
                  }
                );
              } else {
                observable.next(false);
              }
            });
          break;
      }
    });
  }

  private deleteMultipleEntity(
    entityType: PtEntity,
    deleteEntityConfiguration: DeleteEntityConfiguration,
    entities: any[]
  ): Observable<boolean> {
    return new Observable<boolean>((observable: Subscriber<boolean>) => {
      const title = this.translateService.stream('TITLE.DELETE');
      const message = this.translateService.stream(
        'MESSAGE.ARE_YOU_SURE_TO_DELETE_THIS_ELEMENTS'
      );
      const subMessage = [
        this.translateService.stream('MESSAGE.MULTIPLE_DELETION'),
      ];
      this.dialogService
        .runConfirmDialog({
          title,
          message,
          subMessage,
          size: 'md',
          dialogType: DialogType.DELETE,
        })
        .subscribe((confirmed) => {
          if (confirmed) {
            const ids: number[] = entities.map((row) => row.id);
            deleteEntityConfiguration.service.deleteMultiple(ids).subscribe(
              (response) => {
                const numberOfFailed =
                  response.failed && response.failed.length;
                if (numberOfFailed > 0) {
                  const title = this.translateService.stream('TITLE.DELETE');
                  const message = this.translateService.stream(
                    'MESSAGE.DELETE_BY_IDS_FAILED_$',
                    { value: numberOfFailed }
                  );
                  this.dialogService.runConfirmDialog({
                    title,
                    message,
                    dialogType: DialogType.MESSAGE_FAILED,
                  });
                }
                const isValidDelete = ids.length !== numberOfFailed;
                if (isValidDelete) {
                  this.showSuccessfullyDeleteNotification();
                }
                observable.next(isValidDelete);
              },
              () => {
                this.showFailedDeleteNotification();
                observable.next(false);
              }
            );
          } else {
            observable.next(false);
          }
        });
    });
  }

  private deleteSingleDocument(
    documentType: PtEntity,
    documentConfiguration: DeleteEntityConfiguration,
    document: UiDocumentItem
  ): Observable<boolean> {
    if (document?.docAccess === 'READ_ONLY') {
      return this.showMissingPermissionDialog(
        documentConfiguration.title,
        document
      );
    } else {
      return new Observable<boolean>((observable: Subscriber<boolean>) => {
        const subMessage = [];
        this.dialogService
          .runConfirmDialogWithValidation({
            title: documentConfiguration.title,
            message: documentConfiguration.message,
            dialogType: DialogType.DELETE,
            size: 'md',
            subMessage,
          })
          .subscribe((confirmed) => {
            if (confirmed && document?.docId) {
              documentConfiguration.service.delete(document.docId).subscribe({
                next: () => {
                  this.showSuccessfullyDeleteNotification();
                  observable.next(true);
                },
                error: () => {
                  this.showFailedDeleteNotification();
                  observable.next(false);
                },
              });
            } else {
              observable.next(false);
            }
          });
      });
    }
  }

  private showMissingPermissionDialog(
    title: Observable<string>,
    blockedDocument: UiDocumentItem
  ) {
    const subMessageFormat = [of(`<ul><li>${blockedDocument.name}</li></ul>`)];
    return this.dialogService.runConfirmDialog({
      title: title,
      message: this.translateService.stream(
        'MESSAGE.MISSING_USER_PERMISSION_FOR_NEXT_DOCUMENTS_DELETE_SINGLE'
      ),
      subMessage: subMessageFormat,
      dialogType: DialogType.MESSAGE_SUCCESS,
    });
  }

  private deleteMultipleDocument(
    documentType: PtEntity,
    documentConfiguration: DeleteEntityConfiguration,
    documentList: UiDocumentItem[],
    deleteAllVersion?: boolean
  ): Observable<boolean> {
    const blockedDocument = documentList.filter(
      (d) => d?.docAccess === 'READ_ONLY'
    );
    if (blockedDocument.length === documentList.length) {
      return this.showMultiMissingPermissionDialog(
        documentConfiguration.title,
        blockedDocument
      );
    } else {
      return new Observable<boolean>((observable: Subscriber<boolean>) => {
        const subMessage = [];
        subMessage.push(
          this.translateService.stream('MESSAGE.MULTIPLE_DELETION')
        );
        if (blockedDocument.length > 0) {
          this.showMultiMissingPermissionDialog(
            documentConfiguration.title,
            blockedDocument,
            true
          ).subscribe((response) => {
            if (response) {
              this.dialogService
                .runConfirmDialogWithValidation({
                  title: documentConfiguration.title,
                  message: documentConfiguration.message,
                  dialogType: DialogType.DELETE,
                  size: 'md',
                  subMessage,
                })
                .subscribe((confirmed) => {
                  if (confirmed) {
                    const ids = documentList
                      .filter((d) => d?.docAccess !== 'READ_ONLY')
                      .map((row) => row.docId);
                    documentConfiguration.service
                      .deleteMultiple(ids)
                      .subscribe({
                        next: (response: { failed: number[] }) => {
                          if (response) {
                            const numberOfFailed =
                              response?.failed && response.failed?.length;
                            if (numberOfFailed > 0) {
                              const title =
                                this.translateService.stream('TITLE.DELETE');
                              const message = this.translateService.stream(
                                'MESSAGE.DELETE_BY_IDS_FAILED_$',
                                { value: numberOfFailed }
                              );
                              this.dialogService.runConfirmDialog({
                                title,
                                message,
                                dialogType: DialogType.MESSAGE_FAILED,
                              });
                            }
                            const isValidDelete = ids.length !== numberOfFailed;
                            if (isValidDelete) {
                              this.showSuccessfullyDeleteNotification();
                            }
                            observable.next(isValidDelete);
                          } else {
                            // delete not supported?
                            this.showFailedDeleteNotification();
                            observable.next(false);
                          }
                        },
                        error: () => {
                          this.showFailedDeleteNotification();
                          observable.next(false);
                        },
                      });
                  } else {
                    observable.next(false);
                  }
                });
            } else {
              observable.next(false);
            }
          });
        } else {
          this.dialogService
            .runConfirmDialogWithValidation({
              title: documentConfiguration.title,
              message: documentConfiguration.message,
              dialogType: DialogType.DELETE,
              size: 'md',
              subMessage,
            })
            .subscribe((confirmed) => {
              if (confirmed) {
                const ids = documentList
                  .filter((d) => d?.docAccess !== 'READ_ONLY')
                  .map((row) => row.docId);
                documentConfiguration.service.deleteMultiple(ids).subscribe({
                  next: (response: { failed: number[] }) => {
                    if (response) {
                      const numberOfFailed =
                        response?.failed && response.failed?.length;
                      if (numberOfFailed > 0) {
                        const title =
                          this.translateService.stream('TITLE.DELETE');
                        const message = this.translateService.stream(
                          'MESSAGE.DELETE_BY_IDS_FAILED_$',
                          { value: numberOfFailed }
                        );
                        this.dialogService.runConfirmDialog({
                          title,
                          message,
                          dialogType: DialogType.MESSAGE_FAILED,
                        });
                      }
                      const isValidDelete = ids.length !== numberOfFailed;
                      if (isValidDelete) {
                        this.showSuccessfullyDeleteNotification();
                      }
                      observable.next(isValidDelete);
                    } else {
                      // delete not supported?
                      this.showFailedDeleteNotification();
                      observable.next(false);
                    }
                  },
                  error: () => {
                    this.showFailedDeleteNotification();
                    observable.next(false);
                  },
                });
              } else {
                observable.next(false);
              }
            });
        }
      });
    }
  }

  private showMultiMissingPermissionDialog(
    title: Observable<string>,
    blockedDocument: UiDocumentItem[],
    showContinue = false
  ) {
    const li = blockedDocument.map((d) => `<li>${d.name}</li>`);
    const subMessageFormat = [of(`<ul>${li.join('')}</ul>`)];
    const confirmBtnLabel = showContinue
      ? this.translateService.stream('BUTTON.CONTINUE')
      : undefined;
    return this.dialogService.runConfirmDialog({
      title: title,
      message: this.translateService.stream(
        'MESSAGE.MISSING_USER_PERMISSION_FOR_NEXT_DOCUMENTS_DELETE_MULTI'
      ),
      subMessage: subMessageFormat,
      customConfirmBtnLabel: confirmBtnLabel,
      dialogType: showContinue
        ? DialogType.MESSAGE
        : DialogType.MESSAGE_SUCCESS,
    });
  }

  private getDocumentService(
    documentType: PtEntity
  ): DeleteEntityConfiguration {
    switch (documentType) {
      case 'folder':
        return {
          title: this.translateService.stream('TITLE.FOLDER_DELETE'),
          message: this.translateService.stream('MESSAGE.FOLDER_DELETE'),
          service: this.folderService,
        };
      case 'aiAssessment':
        return {
          title: this.translateService.stream('TITLE.AI_ASSESSMENT_DELETE'),
          message: this.translateService.stream('MESSAGE.AI_ASSESSMENT_DELETE'),
          service: this.aiAssessmentService,
        };
      case 'appAssessment':
        return {
          title: this.translateService.stream('TITLE.APP_ASSESSMENT_DELETE'),
          message: this.translateService.stream(
            'MESSAGE.APP_ASSESSMENT_DELETE'
          ),
          service: this.appAssessmentService,
        };
      case 'covidAssessment':
        return {
          title: this.translateService.stream('TITLE.COVID_ASSESSMENT_DELETE'),
          message: this.translateService.stream(
            'MESSAGE.COVID_ASSESSMENT_DELETE'
          ),
          service: this.covidAssessmentsService,
        };
      case 'globalMeasureCheck':
        return {
          title: this.translateService.stream('TITLE.RISK_ASSESSMENT_DELETE'),
          message: this.translateService.stream(
            'MESSAGE.RISK_ASSESSMENT_DELETE'
          ),
          service: this.globalMeasureChecksService,
        };
      case 'treatmentsHolder':
      case 'treatmentsResponsible':
      case 'treatmentsSubResponsible':
      case 'projectCards':
      case 'dpia':
        return {
          title: this.translateService.stream('TITLE.TREATMENT_DELETE'),
          message: this.translateService.stream('MESSAGE.TREATMENT_DELETE'),
          service: this.treatmentService,
        };
    }
    return null;
  }

  private getEntityService(
    entityType: PtEntity,
    singleEntityName?: string
  ): DeleteEntityConfiguration {
    switch (entityType) {
      case 'assessmentQuestion':
        return {
          title: this.translateService.stream(
            'TITLE.ASSESSMENT_QUESTION_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.ASSESSMENT_QUESTION_DELETE'
          ),
          service: this.questionService,
        };
      case 'dataBreach':
        return {
          title: this.translateService.stream('TITLE.DATA_BREACH_DELETE'),
          message: this.translateService.stream('MESSAGE.DATA_BREACH_DELETE'),
          service: this.dataBreachProcessService,
        };
      case 'incidentReport':
        return {
          title: this.translateService.stream(
            'TITLE.DATA_BREACH_NOTIFICATION_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.DATA_BREACH_NOTIFICATION_DELETE'
          ),
          service: this.dataBreachNoticeService,
        };
      // case 'userGroups':
      //   return {
      //     title: this.translateService.stream('TITLE.USER_GROUP_DELETE'),
      //     message: this.translateService.stream('MESSAGE.USER_GROUP_DELETE'),
      //     service: this.userGroupService
      //   }
      case 'attachment':
        return {
          title: this.translateService.stream('TITLE.ATTACHMENT_DELETE'),
          message: this.translateService.stream('MESSAGE.ATTACHMENT_DELETE'),
          service: this.attachmentService,
        };
      case 'applications':
        return {
          title: this.translateService.stream('TITLE.APPLICATIONS_DELETE'),
          message: this.translateService.stream('MESSAGE.APPLICATIONS_DELETE'),
          service: this.applicationService,
        };
      case 'applicationTypes':
        return {
          title: this.translateService.stream('TITLE.APPLICATION_TYPE_DELETE'),
          message: this.translateService.stream(
            'MESSAGE.APPLICATION_TYPE_DELETE'
          ),
          service: this.applicationTypeService,
        };
      case 'legalEntity':
        return {
          title: this.translateService.stream('TITLE.LEGAL_ENTITY_DELETE'),
          message: this.translateService.stream('MESSAGE.LEGAL_ENTITY_DELETE'),
          service: this.legalEntityService,
        };
      case 'organizationalFunctions':
        return {
          title: this.translateService.stream(
            'TITLE.ORGANIZATIONAL_FUNCTIONS_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.ORGANIZATIONAL_FUNCTIONS_DELETE'
          ),
          service: this.organizationalFunctionService,
        };
      case 'userResources':
        return {
          title: this.translateService.stream('TITLE.USER_RESOURCE_DELETE'),
          message: this.translateService.stream('MESSAGE.USER_RESOURCE_DELETE'),
          service: this.usersResourcesService,
        };
      case 'privacyRole':
        return {
          title: this.translateService.stream('TITLE.PRIVACY_ROLE_DELETE'),
          message: this.translateService.stream('MESSAGE.PRIVACY_ROLE_DELETE'),
          service: this.privacyRoleService,
        };
      case 'employmentRelationship':
        return {
          title: this.translateService.stream(
            'TITLE.EMPLOYMENT_RELATIONSHIP_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.EMPLOYMENT_RELATIONSHIP_DELETE'
          ),
          service: this.employmentRelationshipService,
        };

      case 'department':
        return {
          title: this.translateService.stream('TITLE.DEPARTMENT_DELETE'),
          message: this.translateService.stream('MESSAGE.DEPARTMENT_DELETE'),
          service: this.departmentService,
        };
      case 'treatmentCategory':
        return {
          title: this.translateService.stream(
            'TITLE.TREATMENT_CATEGORY_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.TREATMENT_CATEGORY_DELETE'
          ),
          service: this.treatmentCategoryService,
        };
      case 'regulations':
        return {
          title: this.translateService.stream('TITLE.REGULATION_DELETE'),
          message: this.translateService.stream('MESSAGE.REGULATION_DELETE'),
          service: this.regulationService,
        };
      case 'notices':
        return {
          title: this.translateService.stream('TITLE.NOTICES_DELETE'),
          message: this.translateService.stream('MESSAGE.NOTICES_DELETE'),
          service: this.noticeService,
        };
      case 'natureOfData':
        return {
          title: this.translateService.stream('TITLE.NATURE_OF_DATA_DELETE'),
          message: this.translateService.stream(
            'MESSAGE.NATURE_OF_DATA_DELETE'
          ),
          service: this.natureOfDataService,
        };
      case 'interestedPersonCategories':
        return {
          title: this.translateService.stream(
            'TITLE.INTERESTED_PERSON_CATEGORY_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.INTERESTED_PERSON_CATEGORY_DELETE'
          ),
          service: this.interestedPersonCategoryService,
        };
      case 'recipients':
        return {
          title: this.translateService.stream('TITLE.RECIPIENTS_DELETE'),
          message: this.translateService.stream('MESSAGE.RECIPIENTS_DELETE'),
          service: this.recipientService,
        };
      case 'suppliers':
        return {
          title: this.translateService.stream('TITLE.SUPPLIER_DELETE'),
          message: this.translateService.stream('MESSAGE.SUPPLIER_DELETE'),
          service: this.supplierService,
        };
      case 'damage':
        return {
          title: this.translateService.stream('TITLE.DAMAGES_DELETE'),
          message: this.translateService.stream('MESSAGE.DAMAGES_DELETE'),
          service: this.damageService,
        };

      case 'appointmentsResponsible':
        return {
          title: this.translateService.stream(
            'TITLE.APPOINTMENT_RESPONSIBLE_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.APPOINTMENT_RESPONSIBLE_DELETE'
          ),
          service: this.appointmentsResponsibleService,
        };
      case 'appointmentsSystemAdmin':
        return {
          title: this.translateService.stream(
            'TITLE.APPOINTMENT_SYSTEM_ADMIN_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.APPOINTMENT_SYSTEM_ADMIN_DELETE'
          ),
          service: this.appointmentsSystemAdminService,
        };
      case 'appointmentsAuthorized':
        return {
          title: this.translateService.stream(
            'TITLE.APPOINTMENT_AUTHORIZED_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.APPOINTMENT_AUTHORIZED_DELETE'
          ),
          service: this.appointmentsAuthorizedService,
        };
      case 'purposeCategory':
        return {
          title: this.translateService.stream('TITLE.PURPOSE_CATEGORY_DELETE'),
          message: this.translateService.stream(
            'MESSAGE.PURPOSE_CATEGORY_DELETE'
          ),
          service: this.purposeService,
        };
      case 'legalBasis':
        return {
          title: this.translateService.stream('TITLE.LEGAL_BASIS_DELETE'),
          message: this.translateService.stream('MESSAGE.LEGAL_BASIS_DELETE'),
          service: this.legalBasisService,
        };
      case 'organizationalMeasures':
        return {
          title: this.translateService.stream(
            'TITLE.ORGANIZATIONAL_MEASURE_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.ORGANIZATIONAL_MEASURE_DELETE'
          ),
          service: this.organizationalMeasureService,
        };
      case 'organizationalMeasureCategories':
        return {
          title: this.translateService.stream(
            'TITLE.ORGANIZATIONAL_MEASURE_CATEGORY_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.ORGANIZATIONAL_MEASURE_CATEGORY_DELETE'
          ),
          service: this.organizationalMeasureCategoryService,
        };
      case 'preliminaryRiskAssessment':
        return {
          title: this.translateService.stream(
            'TITLE.PRELIMINARY_RISK_ASSESSMENTS_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.PRELIMINARY_RISK_ASSESSMENTS_DELETE'
          ),
          service: this.preliminaryRiskAssessmentService,
        };
      case 'technicalMeasures':
        return {
          title: this.translateService.stream(
            'TITLE.TECHNICAL_MEASURES_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.TECHNICAL_MEASURES_DELETE'
          ),
          service: this.technicalMeasureService,
        };
      case 'technicalMeasureCategories':
        return {
          title: this.translateService.stream(
            'TITLE.TECHNICAL_MEASURE_CATEGORIES_DELETE'
          ),
          message: this.translateService.stream(
            'MESSAGE.TECHNICAL_MEASURE_CATEGORIES_DELETE'
          ),
          service: this.technicalMeasureCategoryService,
        };
      case 'users':
        return {
          title: this.translateService.stream('TITLE.USER_DELETE'),
          message: this.translateService.stream('MESSAGE.USER_DELETE'),
          service: this.usersService,
        };
      case 'consoleUsers':
        return {
          title: this.translateService.stream('TITLE.SUPER_ADMIN_DELETE'),
          message: this.translateService.stream('MESSAGE.SUPER_ADMIN_DELETE', {
            name: singleEntityName,
          }),
          service: this.admUsersService,
        };
      case 'folder':
        return {
          title: this.translateService.stream('TITLE.FOLDER_DELETE'),
          message: this.translateService.stream('MESSAGE.FOLDER_DELETE'),
          service: this.folderService,
        };
      case 'notifications':
        return {
          title: this.translateService.stream('TITLE.REMINDER_DELETE'),
          message: this.translateService.stream('MESSAGE.REMINDER_DELETE'),
          service: this.notificationsService,
        };
      case 'userGroups':
        return {
          title: this.translateService.stream('TITLE.USER_GROUP_DELETE'),
          message: this.translateService.stream('MESSAGE.USER_GROUP_DELETE'),
          service: this.userGroupService,
        };
      case 'permissionRules':
        return {
          title: this.translateService.stream('TITLE.PERMISSION_RULE_DELETE'),
          message: this.translateService.stream(
            'MESSAGE.PERMISSION_RULE_DELETE'
          ),
          service: this.permissionRulesService,
        };
      case 'globalMeasureCheck':
        return {
          title: this.translateService.stream('TITLE.RISK_ASSESSMENT_DELETE'),
          message: this.translateService.stream(
            'MESSAGE.RISK_ASSESSMENT_DELETE'
          ),
          service: this.globalMeasureChecksService,
        };
      default:
        return null;
    }
  }

  private showFailedDeleteNotification() {
    // this.translateService.stream('MESSAGE.DELETE_FAILED').subscribe(
    //   (message) => {
    //     this.store.dispatch(PtCoreActions.showNotification({
    //       notification: {
    //         type: NotificationType.error,
    //         title: '',
    //         message
    //       }
    //     }));
    //   }
    // )
  }

  private showSuccessfullyDeleteNotification() {
    //todo @georgy
    // condizione temporale mentre si migrano le notifiche
    // this.translateService.stream('MESSAGE.SUCCESSFULLY_DELETED').subscribe(
    //   (message) => {
    //     this.store.dispatch(PtCoreActions.showNotification({
    //       notification: {
    //         title: '',
    //         message,
    //         type: NotificationType.success
    //       }
    //     }));
    //   }
    // )
  }
}

interface DeleteEntityConfiguration {
  title: Observable<string>;
  message: Observable<string>;
  service: DeleteEntityServiceInterface;
}
